import { API_ROUTES } from "@/constant";
import AuthAPI from "@/services/Auth/AuthConfig";

const fileReferenceMap = new Map<string, string>();

const isFileReference = (value: unknown): boolean => {
  if (typeof value !== "string" || !value) return false;

  try {
    const decoded = atob(value);
    const data = JSON.parse(decoded);

    return (
      typeof data === "object" &&
      data !== null &&
      typeof data.blobName === "string" &&
      typeof data.iv === "string" &&
      typeof data.authTag === "string" &&
      typeof data.mimeType === "string" &&
      typeof data.originalName === "string"
    );
  } catch {
    return false;
  }
};

const base64ToBuffer = (b64: string): ArrayBuffer => {
  const binary = atob(b64);
  const bytes = new Uint8Array(binary.length);

  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }

  return bytes.buffer;
};

const concatBuffers = (a: ArrayBuffer, b: ArrayBuffer): ArrayBuffer => {
  const out = new Uint8Array(a.byteLength + b.byteLength);

  out.set(new Uint8Array(a), 0);
  out.set(new Uint8Array(b), a.byteLength);

  return out.buffer;
};

const getEncryptionKey = (() => {
  let keyPromise: Promise<CryptoKey> | null = null;

  return (): Promise<CryptoKey> => {
    if (!keyPromise) {
      keyPromise = crypto.subtle.importKey(
        "raw",
        base64ToBuffer(String(process.env.BLOB_ENCRYPTION_KEY)),
        "AES-GCM",
        false,
        ["decrypt"],
      );
    }

    return keyPromise;
  };
})();

const fetchEncryptedFile = async (ref: string) => {
  const { data } = await AuthAPI.get(API_ROUTES.TENANT_FILES_STREAM, {
    params: {
      ref,
    },
  });

  return data;
};

const decryptFile = async (
  fileData: {
    ciphertext: string;
    authTag: string;
    iv: string;
    mimeType: string;
  },
  key: CryptoKey,
): Promise<string> => {
  const combined = concatBuffers(
    base64ToBuffer(fileData.ciphertext),
    base64ToBuffer(fileData.authTag),
  );

  const plainBytes = await crypto.subtle.decrypt(
    {
      name: "AES-GCM",
      iv: base64ToBuffer(fileData.iv),
    },
    key,
    combined,
  );

  const blob = new Blob([plainBytes], {
    type: fileData.mimeType,
  });

  return URL.createObjectURL(blob);
};

const transformFileReference = async (ref: string): Promise<string> => {
  const [fileData, key] = await Promise.all([fetchEncryptedFile(ref), getEncryptionKey()]);

  const url = await decryptFile(fileData, key);

  fileReferenceMap.set(url, ref);

  return url;
};

export const transformUrlFields = async (data: unknown): Promise<unknown> => {
  if (isFileReference(data)) {
    return transformFileReference(String(data));
  }

  if (
    (typeof URLSearchParams !== "undefined" && data instanceof URLSearchParams) ||
    (typeof FormData !== "undefined" && data instanceof FormData)
  ) {
    return data;
  }

  if (Array.isArray(data)) {
    return Promise.all(data.map((item) => transformUrlFields(item)));
  }

  if (data && typeof data === "object") {
    const entries = await Promise.all(
      Object.entries(data).map(async ([key, value]) => {
        return [key, await transformUrlFields(value)];
      }),
    );

    return Object.fromEntries(entries);
  }

  return data;
};

export const restoreFileReferences = (data: unknown): unknown => {
  if (typeof data === "string") {
    return fileReferenceMap.get(data) ?? data;
  }

  if (
    (typeof URLSearchParams !== "undefined" && data instanceof URLSearchParams) ||
    (typeof FormData !== "undefined" && data instanceof FormData)
  ) {
    return data;
  }

  if (Array.isArray(data)) {
    return data.map(restoreFileReferences);
  }

  if (data && typeof data === "object") {
    return Object.fromEntries(
      Object.entries(data).map(([key, value]) => [key, restoreFileReferences(value)]),
    );
  }

  return data;
};
